import {
  ReactNode,
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  useLayoutEffect,
} from 'react'
import type {AppContext, AppInitialProps} from 'next/app'
import App from 'next/app'
import {SpeedInsights} from '@vercel/speed-insights/next'
import {CartProvider, Item, useCart} from '../lib/react-use-cart'
// import Script from 'next/script';
import {createBrowserSupabaseClient} from '@supabase/auth-helpers-nextjs'
import {strip_auth_error_description} from '@/ksebe/utils/authRedirectErrors'
import {
  Session,
  SessionContextProvider,
  useSessionContext,
} from '@supabase/auth-helpers-react'
import {Database} from '../types/supabase'
import {Analytics} from '@vercel/analytics/react'
import {DefaultSeo} from 'next-seo'
import {defaultSEO} from '../next-seo.config'
import {GTM_ID, pageview, gtmAddToCart, gtmRemoveFromCart} from '../lib/gtm'
import {isAppSurfaceRoute} from '../lib/appSurface'
import {
  isAppShellUserAgent,
  shouldWithholdMarketingScripts,
} from '../lib/appShell'
import {isPageviewBlockedRoute, isSensitiveRoute} from '../lib/sensitiveRoutes'
import {
  init_posthog,
  get_posthog,
  on_posthog_ready,
  set_display_language,
  is_identified_visitor,
  suppress_session_replay,
  capture_event,
  capture_initial_pageview,
} from '../lib/posthog'
import {useAttribution} from '../hooks/useAttribution'
import {get_first_touch_person_properties} from '../utils/attribution'
import {TolgeeProvider, useTolgeeSSR, useTranslate} from '@tolgee/react'
import {tolgee} from '../tolgee'
import * as Sentry from '@sentry/nextjs'
import monitoring from '../utils/monitoring'
import {role_person_properties} from '@ksebe/messaging-core'
import {isItemAppointment, parseCartItemForGTM} from '../utils/cartUtils'

// Formbricks - not currently in use, uncomment when needed
// Lazy load Formbricks to reduce initial bundle size (~100-150KB savings)
// const FormbricksInitializer = dynamic(
//   () => import('../components/FormbricksInitializer'),
//   {ssr: false},
// )

// Configure FontAwesome to not auto-add CSS (we'll load it manually for better optimization)
import {config} from '@fortawesome/fontawesome-svg-core'
config.autoAddCss = false

import '../styles/globals.css'
import '@fortawesome/fontawesome-svg-core/styles.css'
import 'tippy.js/dist/tippy.css'

import Link from 'next/link'
import Script from 'next/script'
import {useRouter} from 'next/router'
import {frontend} from '../utils/frontend'
import {should_adopt_cart} from '../utils/cartSync'
import {rehydrate_content_nav} from '../stores/contentNavStore'
import ShoprocketEventListeners from '../components/ShoprocketEventListeners'
import {SnackbarProps} from '@/ksebe/utils/snackbarUtils'
import {SnackbarProvider} from 'notistack'
import PendingQuizResultClaimer from '@/ksebe/components/PendingQuizResultClaimer'
import PromoCopyLinks from '@/ksebe/components/PromoCopyLinks'
import {AppPropsWithLayout} from '@/ksebe/types/TNextLayout'
import {SITE_CONFIG} from '../utils/helpers'

//FIXME convert into api endpoint(s) so that we can handle redis updates from BE
const DELETE_CART_IF_ITS_OLDER_THAN_MS = 15 * 60 * 1000 // 15 minutes
const CHECK_IF_CART_STALE_EVERY_MS = 1 * 60 * 1000 // 1 minute

function ShoppingCardRefresher({children}: {children: ReactNode}) {
  const {items, setItems, emptyCart} = useCart()
  useEffect(() => {
    // An empty cart has nothing reserved, so there is nothing to release. The
    // request this timer used to fire sent `items: []`, and /api/cart's `free`
    // branch runs neither `item?.id` nor `items?.length > 0` for that payload —
    // it did literally nothing.
    //
    // It fired for everyone, not just people with a stale cart: visitors who
    // have never opened a cart have no `cart-update-date`, which reads as
    // Infinity below and always trips the staleness check. So every visitor was
    // POSTing a no-op every 60s from every open tab — a function invocation and
    // one of that endpoint's 60/min rate-limit slots each time.
    if (items.length === 0) return

    const asyncClearCard = async () => {
      // clear shopping card if stale
      const lastModificationDateStr = localStorage.getItem('cart-update-date')
      const differenceInMs =
        lastModificationDateStr != null
          ? new Date().getTime() - new Date(lastModificationDateStr).getTime()
          : Infinity
      if (differenceInMs > DELETE_CART_IF_ITS_OLDER_THAN_MS) {
        try {
          const response = await fetch('/api/cart', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              action: 'free',
              items,
            }),
          })
          if (!response.ok) {
            monitoring.failure('Failed to free cart items', {
              status: response.status,
            })
          }
        } catch (error) {
          monitoring.failure('Error freeing cart items', {error})
        }
        emptyCart()
      }
    }

    asyncClearCard()

    const interval = setInterval(() => {
      asyncClearCard()
    }, CHECK_IF_CART_STALE_EVERY_MS)

    return () => clearInterval(interval) // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
  }, [items, emptyCart])

  // Read inside an event handler that must not re-subscribe on every change.
  // Written from an effect rather than during render: a render React discards
  // or replays must not leak into what the storage handler compares against.
  const items_ref = useRef(items)
  useEffect(() => {
    items_ref.current = items
  }, [items])

  useEffect(() => {
    // Handle storage events from other tabs to sync cart state.
    //
    // Every call to setItems here also POSTs to /api/cart (onSetItems ->
    // updateCartExpiration), and setItems writes the cart back to
    // localStorage — which raises a storage event in the tab we just heard
    // from. With two tabs open that is a ping-pong, and it ran at browser
    // speed: one real Chrome produced 104 535 rejected /api/cart calls in 96
    // seconds, and `refresh` outnumbered `reserve` ~90:1 in the same window.
    // 1.18M of those in a week, all on this route, from 48 IPs — every one a
    // genuine browser. The endpoint's IP rate limit was the only thing
    // containing it.
    //
    // So: only adopt a cart that actually differs from ours. An identical one
    // has nothing to apply, and answering it is what kept the volley alive.
    const handleStorageChange = (event: StorageEvent) => {
      try {
        const apply = (next: Item[]) => {
          if (!should_adopt_cart(next, items_ref.current)) return
          setItems(next)
        }

        if (event.key === null) {
          apply([])
          return
        }
        if (event.key === 'react-use-cart') {
          if (event.newValue == null) {
            apply([])
          } else {
            try {
              const reactCart = JSON.parse(event.newValue)
              apply(reactCart.items ?? [])
            } catch (e) {
              monitoring.failure('Failed to parse cart from localStorage', e)
            }
          }
        }
      } catch (error) {
        monitoring.failure('Error handling storage event', error)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [setItems])
  return <>{children}</>
}

function CartWrapper({children}: {children: ReactNode}) {
  const {t} = useTranslate()

  const updateCartExpiration = async (items: Item[]) => {
    const now = new Date()
    localStorage.setItem('cart-update-date', now.toISOString())

    const response = await fetch('/api/cart', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        action: 'refresh',
        items,
      }),
    })

    if (response.ok) return

    // 429 is the cart endpoint's own rate limit, not a fault. It is keyed on
    // IP (pages/api/cart.ts) and every cart mutation costs two calls, so users
    // sharing an egress address — corporate or carrier-grade NAT — hit it
    // through no fault of their own. Throwing here reported it as an exception
    // and made this the loudest error in the app, which is why
    // utils/frontend.ts had to blanket-mute the `onSetItems` caller.
    //
    // What is actually lost is the server-side reservation TTL refresh, so the
    // slot can expire while the cart still shows it. Worth a warning and worth
    // fixing at the source; not worth an alert, and not worth a throw the
    // callers only swallow anyway.
    if (response.status === 429) {
      monitoring.warning('cart:expiration_rate_limited', {
        status: response.status,
      })
      return
    }

    throw new Error(`Failed to update cart expiration (${response.status})`)
  }

  const cartHandlers = {
    onSetItems: async (items: Item[]) => {
      try {
        await updateCartExpiration(items)
      } catch (error) {
        frontend.failure('onSetItems', error)
      }
    },
    onItemAdd: async (item: Item, allItems: Item[]) => {
      try {
        monitoring.event('onItemAdd start', {item, allItems})

        // Track addition in GTM if it's an appointment item
        if (isItemAppointment(item)) {
          const gtmItem = parseCartItemForGTM(item, t)
          if (gtmItem) {
            try {
              const itemId = JSON.parse(item.id)
              gtmAddToCart({
                ...gtmItem,
                product_id: itemId.productId,
                product_variation_id: itemId.productVariationId,
              })
            } catch (error) {
              monitoring.failure('onItemAdd:gtm_parse_failed', {
                error,
                itemId: item.id,
                price: item.price,
              })
              // Don't rethrow - continue processing other items
            }
          }

          // PostHog: booking_started
          capture_event('booking_started', {
            price: item.price,
          })
        }

        await Promise.all([
          isItemAppointment(item) &&
            fetch('/api/cart', {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({
                action: 'reserve',
                item,
              }),
            }),
          updateCartExpiration(allItems.filter((i: Item) => i.id !== item.id)),
        ])

        monitoring.event('onItemAdd success', {})
      } catch (error) {
        monitoring.failure('onItemAdd failed', {error})
        frontend.failure('onItemAdd', error)
      }
    },
    onItemUpdate: async (payload: object, allItems: Item[]) => {
      try {
        await updateCartExpiration(allItems)
      } catch (error) {
        frontend.failure('onItemUpdate', error)
      }
    },
    onItemRemove: async (id: string, allItems: Item[]) => {
      try {
        // Find the item being removed (still in allItems at this point)
        const removedItem = allItems.find((item) => item.id === id)

        // Track removal in GTM if it's an appointment item
        if (removedItem && isItemAppointment(removedItem)) {
          const gtmItem = parseCartItemForGTM(removedItem, t)
          if (gtmItem) {
            try {
              const itemId = JSON.parse(removedItem.id)
              gtmRemoveFromCart({
                ...gtmItem,
                product_id: itemId.productId,
                product_variation_id: itemId.productVariationId,
              })
            } catch (error) {
              monitoring.failure('onItemRemove:gtm_parse_failed', {
                error,
                itemId: removedItem.id,
                price: removedItem.price,
              })
              // Don't rethrow - continue processing other items
            }
          }
        }

        await Promise.all([
          fetch('/api/cart', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              action: 'free',
              item: {id},
            }),
          }),
          updateCartExpiration(allItems.filter((i: Item) => i.id !== id)),
        ])
      } catch (error) {
        frontend.failure('onItemRemove', error)
      }
    },
  }

  return (
    <CartProvider {...cartHandlers}>
      <ShoppingCardRefresher>{children}</ShoppingCardRefresher>
    </CartProvider>
  )
}

import type {
  SanitySiteNavigation,
  SanityFooter,
  SanitySeoDefaults,
} from '../types/sanity'
import {
  NavDataContext,
  FooterDataContext,
  SeoDefaultsContext,
} from '../contexts/siteDataContext'
import {AppShellContext} from '../contexts/appShellContext'
import {should_adopt, take_handed_session} from '../lib/appSessionHandoff'

type AppOwnProps = {
  /**
   * True when the request came from the native app shell (User-Agent marker).
   * Read server-side so the very first render already withholds the scripts —
   * detecting it client-side would let the consent banner flash before we
   * could suppress it.
   */
  isAppShell: boolean
  navData: SanitySiteNavigation | null
  footerData: SanityFooter | null
  seoDefaultsData: SanitySeoDefaults | null
}

function PostHogInitializer({children}: {children: ReactNode}) {
  const {session, supabaseClient} = useSessionContext()
  const router = useRouter()

  // Initialize PostHog (whenever key is present -- key guards are in init_posthog)
  useEffect(() => {
    const do_init = () => {
      // posthog-js loads asynchronously now, so init_posthog() returns null on
      // this first call and the landing pageview must NOT be gated on it —
      // that pageview carries the URL's utm_*/gclid/fbclid into $initial_*
      // person properties and drives Marketing Analytics attribution.
      // capture_initial_pageview() buffers it, with the URL read at call time,
      // and the site group moved into the library's own init for the same
      // reason.
      init_posthog()
      capture_initial_pageview()
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => do_init(), {timeout: 500})
    } else {
      setTimeout(do_init, 300)
    }
  }, [])

  // Marketing's PostHog popups follow the page locale (sk/en, cs/en), not the
  // browser language.
  useEffect(() => {
    if (!router.locale) return
    return set_display_language(router.locale)
  }, [router.locale])

  // Attribute error-tracking events to the signed-in user so Better Stack can
  // report affected-user counts (its `Users` column reads the SDK `user`
  // payload — without this every error shows 0 and blast radius is invisible).
  // Deliberately id-only: the Supabase UUID is enough to look a case up in the
  // DB, and this is a mental-health product, so patient emails have no business
  // sitting in third-party exception payloads.
  useEffect(() => {
    if (session?.user) {
      Sentry.setUser({id: session.user.id})
    } else {
      Sentry.setUser(null)
    }
  }, [session?.user?.id])

  // Identify / reset user on session change + track login/logout
  useEffect(() => {
    // The library arrives over the network now, so the client may not exist on
    // the first run of this effect — and its deps are the session, which does
    // not change again. Without waiting, a warm session on a slow connection
    // gets no identify() and, worse, no reset() on sign-out, so a shared device
    // keeps using the previous user's distinct_id.
    let cancelled = false
    const stop = on_posthog_ready((client) => {
      const is_identified = is_identified_visitor(client)

      if (session?.user) {
        // User logged in
        if (!is_identified) {
          // Detect login method from user metadata
          const provider =
            session.user.app_metadata?.provider || // OAuth provider (google, facebook, apple)
            (session.user.email ? 'email' : 'unknown')

          capture_event('user_login', {
            login_method: provider, // 'google', 'facebook', 'apple', 'email', etc.
          })
        }

        client.identify(
          session.user.id,
          {
            email: session.user.email,
            site: SITE_CONFIG.name,
            entity: SITE_CONFIG.entity,
          },
          // $set_once: durable first-touch acquisition, so a converting customer
          // carries their original campaign even if the landing $pageview's
          // $initial_* didn't survive (Safari storage clears, identified_only
          // person-creation timing, cross-device).
          get_first_touch_person_properties(),
        )
        // The "Admins" / "Experts" cohorts read these (role_person_properties,
        // the same mapping the admin app uses). Own rows only, by RLS; most
        // users have none. A failure leaves them unset; flags fail closed.
        const user_id = session.user.id
        supabaseClient
          .from('profile_roles')
          .select('role')
          .eq('user_id', user_id)
          .then(({data, error}) => {
            if (cancelled) return
            if (error) {
              monitoring.warning('posthog:roles_read_failed', {
                error: error.message,
              })
              // Fail closed: drop overrides an earlier read set this session.
              client.setPersonPropertiesForFlags(role_person_properties([]))
              return
            }
            const props = role_person_properties(
              (data ?? []).map((r) => r.role),
            )
            client.setPersonProperties(props)
            client.setPersonPropertiesForFlags(props)
          })
      } else {
        // User logged out
        if (is_identified) {
          capture_event('user_logout', {})
        }
        client.reset()
      }
    })
    return () => {
      cancelled = true
      stop()
    }
  }, [session?.user?.id, session?.user?.email, supabaseClient])

  // Page view tracking alongside GTM
  useEffect(() => {
    const handle_route_change = (url: string) => {
      // Dôvera slot/preferences links opt out of analytics entirely, and
      // routeChangeComplete fires BEFORE the destination page's effect — so on
      // a client-side navigation an already-loaded client would capture their
      // tokenised URL before the opt-out lands. Guard centrally.
      //
      // The check-in link is deliberately NOT blocked here: its pageview is the
      // entry point of the questionnaire funnel, and lib/posthog's before_send
      // strips the token from the URL before it is sent.
      // Replay must stop BEFORE the destination page mounts: rrweb would
      // otherwise record at least one commit of the questionnaire DOM, and
      // those buffered frames still flush. The page's own effect is too late.
      if (isSensitiveRoute(url)) suppress_session_replay()
      if (isPageviewBlockedRoute(url)) return
      capture_event('$pageview', {
        $current_url: url,
      })
    }

    router.events.on('routeChangeComplete', handle_route_change)
    return () => {
      router.events.off('routeChangeComplete', handle_route_change)
    }
  }, [router.events])

  // Global error tracking
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Don't track errors in development
      if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
        return
      }

      capture_event('client_error', {
        error_message: event.message,
        error_stack: event.error?.stack?.substring(0, 500), // Limit stack trace length
        page_url: window.location.href,
        line_number: event.lineno,
        column_number: event.colno,
      })
    }

    window.addEventListener('error', handleError)
    return () => window.removeEventListener('error', handleError)
  }, [])

  // Capture UTM attribution from URL
  useAttribution()

  return <>{children}</>
}

// useLayoutEffect on the client, useEffect on the server — the latter only to
// silence React's "useLayoutEffect does nothing on the server" warning; the
// body never runs there.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * Restores the persisted content-nav stack, once, after the first render.
 *
 * Its own component rather than a passenger on an existing one: hung off
 * PostHogInitializer it would stop running the day that gets gated behind
 * cookie consent, and the failure would be a silently missing back-link with
 * no error and no failing test.
 *
 * useLayoutEffect, not useEffect: layout effects flush during commit, while
 * passive effects wait until after paint. In that gap the browser can dispatch
 * a click, and a ReadNextCard push landing before the restore would write a
 * one-entry stack over the reader's real history — persist's merge replaces the
 * stack wholesale rather than unioning it, and every set writes straight
 * through to storage. Committing the restore closes that window. It still
 * happens after the hydration render, which is what keeps the markup matching.
 */
function ContentNavHydrator({children}: {children: ReactNode}) {
  useIsomorphicLayoutEffect(() => {
    rehydrate_content_nav()
  }, [])

  return <>{children}</>
}

export default function MyApp({
  Component,
  pageProps,
  isAppShell,
  navData,
  footerData,
  seoDefaultsData,
}: AppPropsWithLayout<{
  initialSession: Session | null | undefined
}> &
  AppOwnProps) {
  // No `library.add()` here on purpose. It exists only to serve the runtime
  // string-lookup form `icon={['fas', name]}`, which this codebase no longer
  // uses anywhere — that form renders null during SSR and caused a hydration
  // mismatch (fixed in #757 by switching to imported icon objects, see
  // lib/topicIcons.ts). Registering 23 icons here pulled them, and the
  // library machinery, into the bundle EVERY page loads, including the
  // signed-in app surface that shows none of them.
  const [supabaseClient] = useState(() => {
    // Before the client exists: it reads the URL on creation, and a dead
    // sign-in link's error fragment would make it drop the current session.
    strip_auth_error_description()
    return createBrowserSupabaseClient<Database>()
  })

  const router = useRouter()

  // Whether THIS page view gets marketing scripts.
  //
  // Two reasons to withhold: the signed-in account surface (the existing rule)
  // and the native app shell (new). The shell needed adding because it wraps
  // the whole site, so its users meet /login — not an account route — and got
  // the consent banner on every cold start. In a webview that reads as the app
  // redirecting you somewhere, which is exactly what it looked like.
  //
  // GTM matters most here: Waulter is injected BY the tag manager, so
  // withholding GTM is what actually stops the redirect.
  //
  // A third: a route whose URL is a credential (sensitiveRoutes.ts). On a hard
  // landing from an e-mail `routeChangeComplete` never fires, so the redaction
  // there does not run, and GTM / Metricool would read the raw token path.
  const withhold_marketing_scripts =
    shouldWithholdMarketingScripts({
      is_app_surface_route: isAppSurfaceRoute(router.pathname),
      is_app_shell: isAppShell,
    }) || isSensitiveRoute(router.asPath)

  // Detect if we're on the shop page to conditionally load shop-specific scripts
  // Use asPath since /shop is served by Makeswift catch-all route ([...path].tsx)
  // I WANT to include all matches to for example match /shop-xmas25 as well as /shop
  const isShopPage = router.asPath.startsWith('/shop')

  useEffect(() => {
    // GTM gets the raw URL — dataLayer has no equivalent of PostHog's
    // before_send, and whatever tags are installed (GA4, Meta) receive it
    // verbatim. So ALL three Dôvera link routes are withheld here, including
    // the check-in link that PostHog is allowed to measure in redacted form.
    const gtm_pageview = (url: string) => {
      if (isSensitiveRoute(url)) return
      pageview(url)
    }
    router.events.on('routeChangeComplete', gtm_pageview)
    return () => {
      router.events.off('routeChangeComplete', gtm_pageview)
    }
  }, [router.events])

  /**
   * Adopt a session handed in by the native app shell.
   *
   * The app signs in natively and keeps its session in the keychain; a webview
   * has its own cookie jar and would otherwise show the login page to someone
   * already signed in to the app. The shell sets one global before this page's
   * scripts run — see lib/appSessionHandoff.ts for why a global and not a URL
   * fragment or a hand-written cookie.
   *
   * Runs before the sign-in-hooks effect below on purpose: adopting produces a
   * genuine SIGNED_IN, and that listener should see it like any other.
   */
  useEffect(() => {
    const handed = take_handed_session(
      typeof window === 'undefined'
        ? undefined
        : (window as unknown as {[key: string]: unknown}),
    )
    if (!handed) return

    let cancelled = false
    ;(async () => {
      const {
        data: {session},
      } = await supabaseClient.auth.getSession()
      if (cancelled) return
      if (!should_adopt({handed, current_user_id: session?.user.id ?? null})) {
        return
      }

      const {error} = await supabaseClient.auth.setSession(handed)
      if (error) {
        // Worth a log rather than a silent fallback to the login page: from
        // the app this looks like "the Profil tab signed me out", and the
        // cause (an expired token the app failed to refresh) is invisible.
        monitoring.failure('app_shell:session_handoff', error)
      } else {
        monitoring.success('app_shell:session_handoff', undefined)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [supabaseClient])

  // B2B post-auth hooks: runPostAuthHooks runs in pages/auth/callback.tsx for
  // magic-link/OTP/SSO (including last_auth_method for non-SSO flows).
  // Password sign-in and auto-confirmed sign-up never hit that callback, so we
  // POST here — with skip_last_auth_method_update so client SIGNED_IN does not
  // overwrite enterprise_sso / ssojet_otp. Password uses record-login-method.
  useEffect(() => {
    let initial_session_seen = false
    // supabase-js re-emits SIGNED_IN on tab refocus and session recovery, so a
    // user switching tabs fired this POST over and over. The hooks only need
    // running once per session; remembering which one we have already handled
    // removes the repeats (and most of the 403s they generated).
    let hooked_session: string | null = null
    const {
      data: {subscription},
    } = supabaseClient.auth.onAuthStateChange((event, session) => {
      // Skip the initial session restore that fires once on page load — only
      // react to actual fresh sign-ins.
      if (event === 'INITIAL_SESSION') {
        initial_session_seen = true
        return
      }
      if (event !== 'SIGNED_IN') return
      if (!initial_session_seen) {
        // SIGNED_IN before INITIAL_SESSION — defensive; skip the restore-impostor.
        initial_session_seen = true
        return
      }
      if (!session) return

      const session_key = `${session.user.id}:${session.expires_at ?? ''}`
      if (hooked_session === session_key) return
      hooked_session = session_key

      fetch('/api/auth/post-signin-hooks', {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        // An empty POST with no content type is a shape WAFs dislike, and this
        // route is one of the few things Vercel's edge has been 403ing.
        headers: {'Content-Type': 'application/json'},
        body: '{}',
      })
        .then(async (response) => {
          if (response.ok) return

          // The status goes in the message and the body goes in context. Both
          // used to be interpolated into the Error, and Vercel's edge 403 page
          // carries a unique request id (`fra1::…`) — so every occurrence was
          // its own fingerprint, its own issue and its own alert. That is the
          // same trap instrumentation-client.ts already documents for
          // Supabase's throttle countdown.
          const body = await response.text().catch(() => '')
          const context = {
            status: response.status,
            body: body.slice(0, 200),
          }

          // A 4xx here is an edge/config outcome, not our code failing —
          // report it, but do not page anyone with it.
          if (response.status < 500) {
            monitoring.warning('client:post_signin_hooks_fetch', context)
            return
          }
          monitoring.failure(
            'client:post_signin_hooks_fetch',
            new Error(`post_signin_hooks HTTP ${response.status}`),
          )
        })
        .catch((err) =>
          monitoring.failure('client:post_signin_hooks_fetch', err),
        )
    })
    return () => subscription.unsubscribe()
  }, [supabaseClient])

  // Deliberately no `staticData` argument — the instance already holds every
  // locale from init (see tolgee.ts). Passing it again inlined ~173 KB into
  // every page on top of the bundle.
  const ssrTolgee = useTolgeeSSR(
    tolgee,
    router.locale || SITE_CONFIG.defaultLocale,
  )

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page)

  // Merge Sanity SEO defaults over hardcoded config
  const seo_props = useMemo(() => {
    const merged = {...defaultSEO}
    if (seoDefaultsData) {
      if (seoDefaultsData.title_template) {
        merged.titleTemplate = seoDefaultsData.title_template
      }
      if (seoDefaultsData.default_description) {
        merged.description = seoDefaultsData.default_description
      }
    }
    return merged
  }, [seoDefaultsData])

  // The Makeswift runtime is NOT wrapped here: pages/[...path].tsx renders
  // MakeswiftWrapper itself, so it ships only with that page and is loaded
  // before hydration. Wrapping the page here via next/dynamic SSR'd the
  // Makeswift tree but rendered nothing on the client until the chunk arrived,
  // which failed hydration (React #418) on every Makeswift page.
  const page = getLayout(<Component {...pageProps} />)

  return (
    <AppShellContext.Provider value={isAppShell}>
      <NavDataContext.Provider value={navData}>
        <FooterDataContext.Provider value={footerData}>
          <SeoDefaultsContext.Provider value={seoDefaultsData}>
            <SessionContextProvider
              supabaseClient={supabaseClient}
              initialSession={pageProps.initialSession}
            >
              <PostHogInitializer>
                <ContentNavHydrator>
                  {/* <FormbricksInitializer /> */}
                  <SnackbarProvider {...SnackbarProps}>
                    <TolgeeProvider tolgee={ssrTolgee}>
                      <CartWrapper>
                        <DefaultSeo {...seo_props} />
                        <PendingQuizResultClaimer />
                        <PromoCopyLinks />
                        {/* Load GTM only in production (never dev/preview traffic),
                          and never on the signed-in app surface — see
                          lib/appSurface.ts for why. */}
                        {process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' &&
                          !withhold_marketing_scripts && (
                            <Script
                              id="gtag-base"
                              strategy="afterInteractive"
                              dangerouslySetInnerHTML={{
                                __html: `
                      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                      })(window,document,'script','dataLayer', '${GTM_ID}');
                    `,
                              }}
                            />
                          )}
                        {/* CookieYes - loaded lazily to reduce INP impact.
                            Gated on the SHELL only, not on the account surface:
                            AccountFooter renders a `.cky-banner-element` link
                            whose only job is to reopen consent preferences, and
                            that link is dead without this script. Withholding
                            it from a web visitor on /my-account would leave
                            them no way to withdraw consent — which is not ours
                            to take away. The shell is different: it loads no
                            tags at all, so there is no consent to withdraw, and
                            the footer hides the link there. */}
                        {
                          !isAppShell && SITE_CONFIG.isTerapio && (
                            <Script
                              id="cookieyes"
                              type="text/javascript"
                              src="https://cdn-cookieyes.com/client_data/f0153b1d432f9d90b35e1c1b/script.js"
                              strategy="lazyOnload"
                            />
                          )
                          /* Waulter on ksebe */
                          /* <Script
                            id="cookieyes"
                            type="text/javascript"
                            src="https://cdn-cookieyes.com/client_data/32674decfd02067160a56d2d/script.js"
                            strategy="lazyOnload"
                            /> */
                        }

                        {/* Metricool web analytics — production only, and never
                          on the signed-in app surface: it is marketing-site
                          measurement, and the account pages are the shell the
                          mobile app wraps.
                          `onload` fires even when the tracker was neutered by a
                          content blocker or only partially executed, in which
                          case the `beTracker` global is never defined and the
                          bare call threw an unhandled ReferenceError (iOS in
                          particular). Analytics is best-effort — guard the
                          global and swallow the load failure. */}
                        {process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' &&
                          process.env.NEXT_PUBLIC_METRICOOL_HASH &&
                          !withhold_marketing_scripts && (
                            <Script
                              id="metricool"
                              strategy="afterInteractive"
                              dangerouslySetInnerHTML={{
                                __html: `
                      (function(){var s=document.createElement("script");s.src="https://tracker.metricool.com/resources/be.js";s.onload=function(){if(typeof beTracker!=="undefined"&&beTracker&&typeof beTracker.t==="function"){beTracker.t({hash:"${process.env.NEXT_PUBLIC_METRICOOL_HASH}"})}};s.onerror=function(){};document.head.appendChild(s)})();
                    `,
                              }}
                            />
                          )}
                        {/* Shoprocket and Packeta scripts are loaded in Makeswift /shop page embed */}
                        {/* Only load event listeners on shop pages */}
                        {isShopPage && <ShoprocketEventListeners />}
                        <main>
                          {page}
                          {/* Not on a route whose URL is a credential: both
                              report the raw path (sensitiveRoutes.ts). */}
                          {!isSensitiveRoute(router.asPath) && (
                            <SpeedInsights />
                          )}
                        </main>
                        {!isSensitiveRoute(router.asPath) && <Analytics />}
                      </CartWrapper>
                    </TolgeeProvider>
                  </SnackbarProvider>
                </ContentNavHydrator>
              </PostHogInitializer>
            </SessionContextProvider>
          </SeoDefaultsContext.Provider>
        </FooterDataContext.Provider>
      </NavDataContext.Provider>
    </AppShellContext.Provider>
  )
}

MyApp.getInitialProps = async (
  context: AppContext,
): Promise<AppOwnProps & AppInitialProps> => {
  const ctx = await App.getInitialProps(context)

  let nav_data: SanitySiteNavigation | null = null
  let footer_data: SanityFooter | null = null
  let seo_defaults_data: SanitySeoDefaults | null = null

  if (context.ctx.req) {
    const locale_param = context.ctx.locale || SITE_CONFIG.defaultLocale

    // Fetch site data directly from Sanity (avoids HTTP self-fetch anti-pattern).
    //
    // `await import`, NOT `require`. This branch is server-only — it is inside
    // `if (context.ctx.req)` — but a bare `require` is still statically included
    // by the bundler, so @sanity/client and its rxjs/get-it/buffer dependencies
    // shipped to every browser to support code that never runs there. Measured
    // on production: one 249 KB chunk downloaded by every visitor.
    //
    // A dynamic import is emitted as its own chunk and fetched only when this
    // line executes, which on the client is never.
    const {fetch_nav_data, fetch_footer_data, fetch_seo_defaults} =
      await import('../lib/sanity/fetchSiteData')

    const [nav_result, footer_result, seo_result] = await Promise.allSettled([
      fetch_nav_data(locale_param),
      fetch_footer_data(locale_param),
      fetch_seo_defaults(locale_param),
    ])

    nav_data = nav_result.status === 'fulfilled' ? nav_result.value : null
    footer_data =
      footer_result.status === 'fulfilled' ? footer_result.value : null
    seo_defaults_data =
      seo_result.status === 'fulfilled' ? seo_result.value : null
  }

  return {
    ...ctx,
    // `_app.getInitialProps` also runs in the BROWSER on every client-side
    // navigation, where there is no `req`. Reading only the request header
    // therefore flipped this to false on the first in-app link tap, and the
    // site header and account tab bar came back — the exact symptom this flag
    // exists to remove.
    //
    // Server render reads the request; a client transition reads the browser's
    // own User-Agent, which in the shell carries the same marker. One source,
    // two places to read it from.
    isAppShell: isAppShellUserAgent(
      context.ctx.req?.headers['user-agent'] ??
        (typeof navigator === 'undefined' ? null : navigator.userAgent),
    ),
    navData: nav_data,
    footerData: footer_data,
    seoDefaultsData: seo_defaults_data,
  }
}
